import React from 'react';
import { SquaresFour, ListBullets } from '@phosphor-icons/react';

/**
 * `useViewMode(key)` — per-page card ↔ list preference persisted in
 * localStorage so the user's choice sticks across sessions.
 *
 *   const [viewMode, setViewMode] = useViewMode('leads');
 *
 * The `key` is scoped per page so Leads, Quotations, etc. can each remember
 * their own preference independently.
 */
export function useViewMode(key, defaultMode = 'cards') {
  const storageKey = `view_mode:${key}`;
  const [mode, setMode] = React.useState(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      return stored === 'list' || stored === 'cards' ? stored : defaultMode;
    } catch {
      return defaultMode;
    }
  });
  const set = React.useCallback(
    (next) => {
      setMode(next);
      try { localStorage.setItem(storageKey, next); } catch { /* localStorage disabled */ }
    },
    [storageKey]
  );
  return [mode, set];
}

/**
 * `<ViewToggle>` — a two-segment pill (Cards | List). Drop it in the header
 * of any listing page and pair it with `useViewMode`. Uses the same rounded
 * pill styling as the rest of the app for consistency.
 */
export const ViewToggle = ({ mode, onChange, testIdPrefix = 'view' }) => {
  const opts = [
    { id: 'cards', label: 'Cards', Icon: SquaresFour },
    { id: 'list', label: 'List', Icon: ListBullets },
  ];
  return (
    <div
      className="inline-flex items-center bg-[#F4F9F7] border border-[#D5E8E4] rounded-full p-1"
      role="group"
      aria-label="View mode"
      data-testid={`${testIdPrefix}-toggle`}
    >
      {opts.map(({ id, label, Icon }) => {
        const active = mode === id;
        return (
          <button
            key={id}
            type="button"
            onClick={() => onChange(id)}
            data-testid={`${testIdPrefix}-toggle-${id}`}
            aria-pressed={active}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition ${
              active
                ? 'bg-white text-[#00a69c] shadow-sm'
                : 'text-[#6D8E87] hover:text-[#052A23]'
            }`}
          >
            <Icon size={14} weight={active ? 'fill' : 'regular'} />
            {label}
          </button>
        );
      })}
    </div>
  );
};
